import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CheckoutReviewAndSubmitPageContext } from "@insite/content-library/Pages/CheckoutReviewAndSubmitPage";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import setCustomerReference4Error from "@tKgpco/Store/Data/CheckoutReviewAndSubmit/Handlers/CustomerReference4ErrorHandler";
import setcustomerReference4 from "@tKgpco/Store/Data/CheckoutReviewAndSubmit/Handlers/CustomerReference4Handler";

import { TYPOGRAPHY } from "@tKgpco/theme/design";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomApplicationState) => {
    const cartState: any = getCurrentCartState(state).value;

    return {
        cartState,
        currentBillTo: getBillToState(state, cartState?.billToId).value,
        customerReference4Error: state.KGPState.CheckoutReview.customerReference4Error,
        customerReference4: state.KGPState.CheckoutReview.customerReference4,
    };
};

const mapDispatchToProps = {
    setcustomerReference4,
    setCustomerReference4Error,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CheckoutShippingCarrierServiceStyles {
    customerReferenceGrid?: GridItemProps;
    customerReferenceChildGrid?: GridItemProps;
    customerReferenceSelect?: SelectPresentationProps;
    customerOrderReferenceLabelStyle?: InjectableCss;
}

export const checkoutShippingCarrierServiceStyles: CheckoutShippingCarrierServiceStyles = {
    customerReferenceGrid: {
        width: 12,
        css: css`
            padding: 0px 5px 3px 16px;
            label {
                font-size: ${TYPOGRAPHY.size.smallest};
            }
        `,
    },
    customerReferenceChildGrid: {
        width: 12,
        css: css`
            padding: 0px 5px 20px 16px;
            label {
                font-size: ${TYPOGRAPHY.size.smallest};
            }
        `,
    },
    customerOrderReferenceLabelStyle: {
        css: css`
            font-size: ${TYPOGRAPHY.size.smallest};
            font-weight: 600;
        `,
    },
};

export const checkoutShippingCarrierService = checkoutShippingCarrierServiceStyles;
const styles = checkoutShippingCarrierServiceStyles;

const CustomerReference4Field: FC<Props> = ({
    setcustomerReference4,
    currentBillTo,
    customerReference4,
    customerReference4Error,
    setCustomerReference4Error,
}) => {
    const [properties, setProperties] = useState(currentBillTo?.properties);
    const [customerRefChild, setCustomerRefChild] = useState(customerReference4 || "");
    const [customerReference4Val, setCustomerReference4Val] = useState(customerReference4 || "");
    const [customerRefError, setCustomerRefError] = useState(
        properties?.customerOrderReferenceRequired4?.toLowerCase() === "true" ? customerReference4Error : "",
    );

    useEffect(() => {
        properties?.customerOrderReferenceRequired4?.toLowerCase() === "true" &&
            setCustomerRefError(customerReference4Error);
    }, [customerReference4Error]);

    useEffect(() => {
        setProperties(currentBillTo?.properties);
    }, [currentBillTo?.properties]);

    useEffect(() => {
        setcustomerReference4({ customerReference4: customerReference4Val || "" });
    }, [customerReference4Val]);

    useEffect(() => {
        setcustomerReference4({ customerReference4: customerRefChild || "" });
    }, [customerRefChild]);

    const customerReferenceDropDownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const error =
            e.target.value.trim() === ""
                ? `${siteMessage(properties?.customerOrderReferenceLabel4 ?? "")} is required`
                : "";

        setCustomerRefChild(e.target.value);
        setcustomerReference4({ customerReference4: e.target.value });
        if (properties?.customerOrderReferenceRequired4?.toLowerCase() === "true") {
            setCustomerReference4Error({ customerReference4Error: error });
        }
    };

    const customerReferenceInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const error =
            e.target.value.trim() === ""
                ? `${siteMessage(properties?.customerOrderReferenceLabel4 ?? "")} is required`
                : "";

        setCustomerReference4Val(e.target.value);
        setcustomerReference4({ customerReference4: e.target.value });
        if (properties?.customerOrderReferenceRequired4?.toLowerCase() === "true") {
            setCustomerReference4Error({ customerReference4Error: error });
        }
    };

    if (!properties?.customerOrderReferenceType4 || properties?.customerOrderReferenceAttribute4 === "None") {
        return null;
    }

    return (
        <GridContainer>
            <GridItem {...styles.customerReferenceChildGrid}>
                {properties?.customerOrderReferenceType4 === "Dropdown" ? (
                    <Select
                        {...styles.customerReferenceSelect}
                        label={properties.customerOrderReferenceLabel4}
                        value={customerRefChild}
                        onChange={customerReferenceDropDownChange}
                        ref={null}
                        required={properties?.customerOrderReferenceRequired4?.toLowerCase() === "true"}
                        error={
                            properties?.customerOrderReferenceRequired4?.toLowerCase() === "true" && customerRefError
                        }
                    >
                        <option value="">{translate("Select option")}</option>
                        {properties?.listReferencesValues4.split("|").map(method => (
                            <option key={method} value={method}>
                                {method}
                            </option>
                        ))}
                    </Select>
                ) : (
                    <TextField
                        label={properties?.customerOrderReferenceLabel4}
                        value={customerReference4Val}
                        onChange={customerReferenceInputChange}
                        required={properties?.customerOrderReferenceRequired4?.toLowerCase() === "true"}
                        error={
                            properties?.customerOrderReferenceRequired4?.toLowerCase() === "true" && customerRefError
                        }
                    />
                )}
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CustomerReference4Field),
    definition: {
        displayName: "Customer Reference 4 Field",
        group: "Checkout - Review & Submit",
        allowedContexts: [CheckoutReviewAndSubmitPageContext],
    },
};

export default widgetModule;
